<template>
  <div class="unsplash-container" :style="{ minHeight: containerHeight }">
    <!-- 搜索栏 -->
    <div class="search-section">
      <h1 class="app-title">Unsplash 图片浏览器</h1>
      <div class="search-bar">
        <div class="search-input-wrapper">
          <input
            v-model="searchQuery"
            type="text"
            class="search-input"
            placeholder="搜索图片，如 'nature'"
            @keyup.enter="handleSearch"
          />
        </div>
        <button class="search-button" :disabled="loading" @click="handleSearch">
          <span>搜索</span>
        </button>
      </div>
      <div v-if="!initialLoad" class="page-info">
        结果: <span>{{ totalImages }}</span> 页数:
        <span>{{ page }} / {{ totalPages }}</span>
      </div>
    </div>

    <!-- 图片网格 -->
    <div class="image-grid-container">
      <table class="image-grid">
        <tbody>
          <tr v-for="row in 4" :key="row">
            <td v-for="col in 5" :key="col" class="image-cell">
              <template v-if="getImageByPosition(row, col)">
                <div
                  class="image-wrapper"
                  @click="openPreview(getImageByPosition(row, col)?.urls.full)"
                >
                  <img
                    :src="getImageByPosition(row, col)?.urls.small"
                    :alt="
                      getImageByPosition(row, col)?.alt_description ||
                      'Unsplash图片'
                    "
                    loading="lazy"
                    class="grid-image"
                  />
                  <div class="image-overlay">
                    <div class="action-buttons">
                      <button
                        @click.stop="
                          downloadImage(getImageByPosition(row, col))
                        "
                        class="action-button download"
                        title="下载图片"
                      >
                        <svg viewBox="0 0 24 24">
                          <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z" />
                        </svg>
                      </button>
                    </div>
                  </div>
                  <div class="image-info">
                    <span class="author-name">{{
                      getImageByPosition(row, col)?.user?.name || "未知作者"
                    }}</span>
                    <span class="image-dimensions">{{
                      formatDimensions(getImageByPosition(row, col))
                    }}</span>
                  </div>
                </div>
              </template>
              <template v-else-if="images.length < row * 5 && hasMoreImages">
                <div class="empty-cell">
                  <div class="loading-spinner" v-if="loading"></div>
                </div>
              </template>
              <template v-else>
                <div class="empty-cell" style="background: transparent"></div>
              </template>
            </td>
          </tr>
        </tbody>
      </table>

      <!-- 空白状态 -->
      <div
        v-if="!loading && images.length === 0 && !initialLoad"
        class="empty-state"
      >
        <div class="empty-icon">
          <svg viewBox="0 0 24 24">
            <path
              d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"
            />
          </svg>
        </div>
        <h3>没有找到相关图片</h3>
        <p>请尝试其他搜索关键词</p>
      </div>
    </div>

    <div v-if="totalPages > 1" class="pagination">
    <button
      class="page-button"
      :disabled="page === 1 || loading"
      @click="prevPage"
    >
      上一页
    </button>
    <div class="page-numbers">
      <template v-for="(item, index) in paginationDisplay" :key="index">
        <div v-if="item === '...'" class="ellipsis">...</div>
        <button
          v-else
          class="page-number"
          :class="{ active: item === page }"
          @click="goToPage(item)"
        >
          {{ item }}
        </button>
      </template>
    </div>
    <button
      class="page-button"
      :disabled="page === totalPages || loading"
      @click="nextPage"
    >
      下一页
    </button>
  </div>

    <!-- 图片预览 -->
    <PhotoView
      v-if="showPreview"
      v-model:visible="showPreview"
      :src="selectedImage"
      :disable-scroll="false"
    />
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import PhotoView from "vue3-photo-view";
import axios from "axios";
const searchQuery = ref("");
const images = ref([]);
const page = ref(1);
const totalPages = ref(1);
const totalImages = ref(0);
const loading = ref(false);
const showPreview = ref(false);
const selectedImage = ref("");
const initialLoad = ref(true);

const hasMoreImages = computed(() => {
  return (
    page.value < totalPages.value || images.value.length < totalImages.value
  );
});

const paginationDisplay = computed(() => {
  const current = page.value;
  const total = totalPages.value;
  
  if (total <= 5) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }
  
  // 特殊处理前两页
  if (current <= 2) {
    if (current === 1) return [1, 2, '...', total];
    return [1, 2, '...', total];
  }
  
  // 中间页码的处理
  if (current >= 3 && current <= total - 2) {
    return [1, '...', current, '...', total];
  }
  
  // 最后两页的处理
  if (current >= total - 1) {
    if (current === total) return [1, '...', total - 1, total];
    return [1, '...', total - 1, total];
  }
  
  return [];
});
const getImageByPosition = (row, col) => {
  const index = (row - 1) * 5 + col - 1;
  return index < images.value.length ? images.value[index] : null;
};

const formatDimensions = (image) => {
  if (!image || !image.width || !image.height) return "";
  return `${image.width} × ${image.height}`;
};

const fetchRandomImages = async () => {
  loading.value = true;
  initialLoad.value = true;
  try {
    const response = await fetch(`${process.env.VUE_APP_API_BASE}/api/random`);
    const data = await response.json();
    images.value = data.images;
    totalImages.value = data.total;
    totalPages.value = data.pages;
  } catch (error) {
    console.error("获取随机图片失败:", error);
  } finally {
    loading.value = false;
    initialLoad.value = false;
  }
};

const handleSearch = async () => {
  if (!searchQuery.value.trim()) {
    await fetchRandomImages();
    return;
  }

  loading.value = true;
  initialLoad.value = false;
  page.value = 1;
  await fetchImages();
};

const downloadImage = async (image) => {
  if (!image) return;

  try {
    const response = await axios.get(image.urls.raw, {
      responseType: "blob",
      params: {
        w: 1920,
        auto: "format",
        fit: "max",
      },
    });

    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement("a");
    link.href = url;
    const fileName = `unsplash-${image.id}.jpg`;
    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } catch (error) {
    console.error("下载图片失败:", error);
    alert("下载图片失败");
  }
};

const openPreview = (url) => {
  selectedImage.value = url;
  showPreview.value = true;
};

const nextPage = async () => {
  if (page.value >= totalPages.value || loading.value) return;
  page.value++;
  await fetchImages(false);
};

const prevPage = async () => {
  if (page.value <= 1 || loading.value) return;
  page.value--;
  await fetchImages(false);
};

const goToPage = async (n) => {
  if (n < 1 || n > totalPages.value || n === page.value || loading.value)
    return;
  page.value = n;
  await fetchImages(false);
};
const fetchImages = async (showLoading = true) => {
  if (showLoading) loading.value = true;

  try {
    const response = await fetch(
      `${process.env.VUE_APP_API_BASE}/api/search?query=${encodeURIComponent(
        searchQuery.value
      )}&page=${page.value}`
    );
    const data = await response.json();

    images.value = data.images;
    totalImages.value = data.total;
    totalPages.value = data.pages;
  } catch (error) {
    console.error("获取图片失败:", error);
    images.value = [];
    totalImages.value = 0;
    totalPages.value = 1;
  } finally {
    loading.value = false;
  }
};

onMounted(() => {
  fetchRandomImages();
});
</script>

<style scoped>
.unsplash-container {
  max-width: 600px;
  margin: 10px auto;
  padding: 8px;
  background: #f8f9fa;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  width: 90vw;
}

.search-section {
  margin-bottom: 8px;
}

.search-bar {
  display: flex;
  gap: 8px;
  margin-bottom: 6px;
}

.search-input {
  flex: 1;
  padding: 6px 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
}

.search-button {
  padding: 6px 12px;
  background: #4361ee;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  margin: 0;
}

.search-button:disabled {
  background: #e0e0e0; /* 置灰背景 */
  color: #9e9e9e; /* 置灰文本 */
  cursor: not-allowed; /* 禁用鼠标指针 */
}

.page-info {
  font-size: 12px;
  color: #666;
  text-align: center;
  margin-top: 6px;
}

.image-grid-container {
  margin: 8px 0;
  max-height: 40vh;
}

.image-grid {
  display: table;
  width: 100%;
  border-collapse: collapse;
}

.image-cell {
  position: relative;
  width: 20%;
  height: 0;
  padding-bottom: 15%;
}

.image-wrapper {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  margin: 2px;
  border-radius: 4px;
  overflow: hidden;
}

.grid-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s;
}

.image-wrapper:hover {
  transform: scale(1.03);
  z-index: 2;
}

.image-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.3);
  opacity: 0;
  transition: all 0.2s;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 4px;
}

.image-wrapper:hover .image-overlay {
  opacity: 1;
}

.action-buttons {
  display: flex;
  justify-content: flex-end;
  gap: 4px;
  z-index: 2;
}

.action-button {
  width: 24px;
  height: 24px;
  background: rgba(255, 255, 255, 0.8);
  border: none;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  margin-top: 50px;
}

.action-button svg {
  width: 22px;
  height: 22px;
}

.download svg {
  fill: #333;
}

.image-info {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: rgba(0, 0, 0, 0.6);
  color: white;
  padding: 4px;
  font-size: 9px;
}

.author-name {
  display: block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  margin: 12px 0;
  padding: 6px;
  height: 42px;
  box-sizing: border-box;
}

.page-button {
  min-width: 60px;
  padding: 5px 10px;
  background: #4361ee;
  color: white;
  border: none;
  border-radius: 4px;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
}

.page-button[disabled] {
  background: #e0e0e0;
  color: #9e9e9e;
  cursor: not-allowed;
}

.page-number {
  min-width: 32px;
  padding: 5px 0;
  background: white;
  color: #333;
  border: 1px solid #ddd;
  border-radius: 4px;
  text-align: center;
  font-weight: bold;
}

.page-number.active {
  background: #3a56d4;
  color: white;
  border-color: #3a56d4;
}

.page-numbers {
  display: flex;
  gap: 6px;
  overflow-x: auto;
  max-width: calc(100% - 140px);
  padding: 0 4px;
  scrollbar-width: none;
}

.page-numbers::-webkit-scrollbar {
  display: none;
}

.ellipsis {
  display: flex;
  align-items: center;
  padding: 0 4px;
}

.empty-cell {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  margin: 2px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.loading-spinner {
  width: 20px;
  height: 20px;
  border: 2px solid rgba(0, 0, 0, 0.1);
  border-top-color: #4361ee;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.empty-state {
  text-align: center;
  padding: 20px;
  color: #666;
  position: relative;
  bottom: 250px;
}

.empty-icon svg {
  width: 48px;
  height: 48px;
  fill: #aaa;
}

@media (max-width: 600px) {
  .unsplash-container {
    width: auto;
    margin: 0;
    padding: 0;
  }
  .action-button {
    margin: 30px -4px 0 0;
  }
  .search-bar {
    flex-direction: column;
  }

  .image-cell {
    padding-bottom: 20%;
  }

  .page-button {
    min-width: 50px;
    padding: 3px 6px;
  }

  .page-numbers {
    max-width: calc(100% - 120px);
  }
}
</style>
